import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePayroll } from '../hooks/usePayroll';
import { useShiftManager } from '../hooks/useShiftManager';
import { db, functions } from '../services/firebase';
import { collection, getDocs, orderBy, query, where, Timestamp } from 'firebase/firestore';
// Importar funciones de formato y utilidades de zona horaria desde utils/formatters.js
import {
  formatCurrency,
  formatDuration,
  formatDateInPR,
  getCurrentPRTime, // Importar getCurrentPRTime
  utcToPuertoRicoTime // Importar utcToPuertoRicoTime
} from '../utils/formatters';
import {
  LogOut, Terminal, Loader2, Users, Filter, Clock, Tag, Plus, Edit, X,
  Download, Eye, ChevronDown, ChevronUp, FileSpreadsheet, BarChart3,
  TrendingUp, Activity, Calendar, DollarSign, ShoppingBag, Zap,
  Calculator, LayoutDashboard
} from 'lucide-react';
import { format as formatDateFns } from 'date-fns'; // Renombrado para no confundir con formatDateInPR
import { httpsCallable } from 'firebase/functions'; //
import DashboardHeader from '../components/dashboard/DashboardHeader';
import DatePresetButtons, { getPresetRange, DEFAULT_PRESET } from '../components/common/DatePresetButtons';

// NOTA: Las funciones de zona horaria y el offset ya están en formatters.js
// Eliminamos las duplicadas de aquí:
// const PR_TIMEZONE_OFFSET = -4; 
// const utcToPuertoRicoTime = (utcDate) => { ... };
// const puertoRicoTimeToUtc = (prDate) => { ... };
// const getCurrentPRTime = () => { ... };

// === SIMULACIÓN TEMPORAL DE useToast ===
const useToast = () => {
  const showToast = (type, message) => {
    console.log(`[Toast ${type.toUpperCase()}]: ${message}`);
  };
  return { showToast };
};

// === COMPONENTE MODAL PARA CREAR/EDITAR TURNOS ===
const ShiftEditorModal = ({ isOpen, onClose, shift, employees, onSave, preselectedEmployeeId }) => {
  const shiftManager = useShiftManager();
  const { loading, error, success, resetStatus } = shiftManager;
  const { showToast } = useToast();

  const [employeeId, setEmployeeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [initialized, setInitialized] = useState(false);

  const isEditing = shift !== null;

  useEffect(() => {
    if (isOpen && !initialized) {
      if (isEditing && shift) {
        // Al editar un turno existente:
        // Convertir el Timestamp UTC de Firestore a la hora local de PR para mostrar en los inputs
        const startPR = utcToPuertoRicoTime(shift.startTime.toDate()); // Usar la función importada
        const endPR = utcToPuertoRicoTime(shift.endTime.toDate()); // Usar la función importada

        setEmployeeId(shift.employeeId);
        setStartDate(formatDateFns(startPR, 'yyyy-MM-dd'));
        setStartTime(formatDateFns(startPR, 'HH:mm'));
        setEndDate(formatDateFns(endPR, 'yyyy-MM-dd'));
        setEndTime(formatDateFns(endPR, 'HH:mm'));
      } else {
        // Al crear un nuevo turno:
        // Inicializar con la fecha/hora actual en la zona horaria de PR
        const nowPR = getCurrentPRTime(); // Usar la función importada
        setEmployeeId(preselectedEmployeeId || employees[0]?.id || '');
        setStartDate(formatDateFns(nowPR, 'yyyy-MM-dd'));
        setStartTime(formatDateFns(nowPR, 'HH:mm'));
        setEndDate(formatDateFns(nowPR, 'yyyy-MM-dd'));
        setEndTime(formatDateFns(nowPR, 'HH:mm'));
      }
      setInitialized(true);
    }

    if (!isOpen && initialized) {
      setInitialized(false);
    }
  }, [isOpen, initialized, isEditing, shift, employees, preselectedEmployeeId]);

  useEffect(() => {
    if (success) {
      showToast('success', `¡Turno ${isEditing ? 'actualizado' : 'creado'} correctamente!`);
      const timer = setTimeout(() => {
        onClose();
        resetStatus();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success, onClose, resetStatus, isEditing, showToast]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!employeeId) {
      showToast('error', 'Por favor seleccione un empleado.');
      return;
    }

    // Crear objetos Date a partir de las cadenas de input.
    // Estos objetos Date se crearán en la zona horaria LOCAL del navegador.
    // useShiftManager.js ya se encarga de convertir esto a PR_TIMEZONE y luego a UTC para Firestore.
    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);

    if (endDateTime <= startDateTime) {
      showToast('error', 'La hora de fin debe ser posterior a la hora de inicio.');
      return;
    }

    const selectedEmployee = employees.find(emp => emp.id === employeeId);

    const shiftData = {
      employeeId,
      employeeName: selectedEmployee?.name || 'Empleado Desconocido',
      startTime: startDateTime,
      endTime: endDateTime,
      hourlyRate: selectedEmployee?.hourlyRate || 10.50,
    };

    let successResult = false;
    try {
      if (isEditing) {
        successResult = await shiftManager.updateShift(shift.id, shiftData);
      } else {
        successResult = await shiftManager.createShift(shiftData);
      }

      if (successResult) {
        onSave(shiftData);
      }
    } catch (err) {
      showToast('error', `Error al guardar turno: ${err.message || 'Desconocido'}`);
    }
  };

  const handleClose = () => {
    resetStatus();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-900 border border-white/20 rounded-2xl p-8 w-full max-w-lg relative">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition"
        >
          <X size={24} />
        </button>
        <h2 className="text-2xl font-bold mb-6 text-white">
          {isEditing ? 'Editar Turno' : 'Crear Nuevo Turno'}
        </h2>

        {success ? (
          <div className="text-center py-10">
            <div className="mb-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <p className="text-green-400 text-lg font-semibold">
                ¡Turno {isEditing ? 'actualizado' : 'creado'} correctamente!
              </p>
              <p className="text-white/60 text-sm mt-2 mb-4">
                El reporte se actualizará automáticamente...
              </p>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-green-600 rounded-lg text-white hover:bg-green-700 transition"
              >
                Cerrar
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Empleado</label>
              <select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                disabled={isEditing}
                className="w-full bg-white/10 border border-white/20 rounded-lg py-3 px-4 focus:ring-2 focus:ring-white/50 focus:outline-none disabled:opacity-50 text-white"
                required
              >
                <option value="">Seleccionar empleado...</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Inicio</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-3 mb-2 focus:ring-2 focus:ring-white/50 focus:outline-none text-white"
                  required
                />
                <input
                  type="time"
                  value={startTime}
                  onChange={e => setStartTime(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-3 focus:ring-2 focus:ring-white/50 focus:outline-none text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Fin</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-3 mb-2 focus:ring-2 focus:ring-white/50 focus:outline-none text-white"
                  required
                />
                <input
                  type="time"
                  value={endTime}
                  onChange={e => setEndTime(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-3 focus:ring-2 focus:ring-white/50 focus:outline-none text-white"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="text-red-400 text-sm text-center bg-red-500/10 p-3 rounded-lg">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !employeeId}
              className="w-full py-3 bg-green-600 rounded-lg font-semibold hover:bg-green-700 transition disabled:opacity-50 flex items-center justify-center text-white"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                isEditing ? 'Actualizar Turno' : 'Crear Turno'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// === COMPONENTE PARA DESGLOSE DETALLADO DE TURNOS ===
const DetailedShiftsModal = ({ isOpen, onClose, employee, shifts, onEditShift }) => {
  if (!isOpen || !employee) return null;

  const totalHours = shifts.reduce((acc, shift) => acc + (shift.durationMinutes / 60), 0);
  const totalEarnings = shifts.reduce((acc, shift) => acc + (shift.earnings || 0), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-white/20 rounded-2xl p-8 w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">Turnos Detallados - {employee.employeeName}</h2>
            <p className="text-white/60 mt-1">
              {shifts.length} turnos • {formatDuration(totalHours)} • {formatCurrency(totalEarnings)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          <div className="space-y-3">
            {shifts.map((shift, index) => (
              <div key={shift.id || index} className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-2">
                      <span className="text-lg font-semibold text-white">
                        Turno #{index + 1}
                      </span>
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
                        {shift.status === 'completed' ? 'Completado' : 'Activo'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-white/60">Fecha</p>
                        {/* Usar formatDateInPR para mostrar la fecha en la zona horaria de PR */}
                        <p className="font-medium text-white">
                          {formatDateInPR(shift.startTime, 'dd/MM/yyyy')}
                        </p>
                      </div>

                      <div>
                        <p className="text-white/60">Hora de Entrada</p>
                        {/* Se cambia el formato de la hora de 'HH:mm' a 'hh:mm a' para mostrar el horario de 12 horas */}
                        <p className="font-medium text-white">
                          {formatDateInPR(shift.startTime, 'hh:mm a')}
                        </p>
                      </div>

                      <div>
                        <p className="text-white/60">Hora de Salida</p>
                        {/* Se cambia el formato de la hora de 'HH:mm' a 'hh:mm a' para mostrar el horario de 12 horas */}
                        <p className="font-medium text-white">
                          {shift.endTime ? formatDateInPR(shift.endTime, 'hh:mm a') : 'En progreso'}
                        </p>
                      </div>

                      <div>
                        <p className="text-white/60">Duración</p>
                        <p className="font-medium text-white">
                          {formatDuration(shift.durationMinutes / 60)}
                        </p>
                      </div>

                      <div>
                        <p className="text-white/60">Tarifa/Hora</p>
                        <p className="font-medium text-white">
                          {formatCurrency(employee.hourlyRate || 10.50)}
                        </p>
                      </div>

                      <div>
                        <p className="text-white/60">Ganancias</p>
                        <p className="font-medium text-green-400">
                          {formatCurrency(shift.earnings || 0)}
                        </p>
                      </div>

                      <div>
                        <p className="text-white/60">Creado</p>
                        {/* Usar formatDateInPR para mostrar la hora de creación en la zona horaria de PR */}
                        <p className="font-medium text-xs text-white">
                          {formatDateInPR(shift.createdAt, 'dd/MM hh:mm a')}
                        </p>
                      </div>

                      <div>
                        <p className="text-white/60">Modificado</p>
                        {/* Usar formatDateInPR para mostrar la hora de modificación en la zona horaria de PR */}
                        <p className="font-medium text-xs text-white">
                          {shift.updatedAt ? formatDateInPR(shift.updatedAt, 'dd/MM hh:mm a') : 'N/A'}
                        </p>
                      </div>
                    </div>

                    {shift.editedBy && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <p className="text-xs text-white/50">
                          Editado por: {shift.editedBy}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <button
                      onClick={() => onEditShift(shift)}
                      className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition"
                      title="Editar turno"
                    >
                      <Edit size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-white/20">
          <div className="flex justify-between items-center">
            <div className="text-sm text-white/60">
              Total de {shifts.length} turnos en el período seleccionado
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition text-white"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// === COMPONENTE PARA ESTADÍSTICAS EJECUTIVAS ===
const ExecutiveDashboard = ({ reportSummary, channelStats, periodStats, isVisible, onToggle }) => {
  if (!isVisible) {
    return (
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-8">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between p-4 hover:bg-white/5 rounded-lg transition"
        >
          <div className="flex items-center gap-3">
            <BarChart3 className="w-6 h-6 text-white/70" />
            <h3 className="text-lg font-semibold text-white">Dashboard Ejecutivo</h3>
          </div>
          <ChevronDown className="w-5 h-5 text-white/50" />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-white/70" />
          <h3 className="text-lg font-semibold text-white">Dashboard Ejecutivo</h3>
        </div>
        <button
          onClick={onToggle}
          className="p-2 hover:bg-white/10 rounded-lg transition"
        >
          <ChevronUp className="w-5 h-5 text-white/50" />
        </button>
      </div>

      {/* Métricas Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="w-8 h-8 text-green-400" />
            <div>
              <p className="text-white/60 text-sm">Total a Pagar</p>
              <p className="text-2xl font-bold text-green-400">
                {formatCurrency(reportSummary?.totalToPay || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-8 h-8 text-blue-400" />
            <div>
              <p className="text-white/60 text-sm">Horas Totales</p>
              <p className="text-2xl font-bold text-white">
                {formatDuration(reportSummary?.totalHours || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-8 h-8 text-purple-400" />
            <div>
              <p className="text-white/60 text-sm">Comisiones</p>
              <p className="text-2xl font-bold text-purple-400">
                {formatCurrency(reportSummary?.totalCommissions || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-8 h-8 text-orange-400" />
            <div>
              <p className="text-white/60 text-sm">Empleados Activos</p>
              <p className="text-2xl font-bold text-white">
                {reportSummary?.totalEmployees || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Breakdown por Canal */}
      {channelStats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2 text-white">
              <ShoppingBag className="w-5 h-5" />
              Breakdown por Canal
            </h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-white">Punto de Venta (POS)</p>
                  <p className="text-sm text-white/60">{channelStats.pos.orders} órdenes</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-white">{formatCurrency(channelStats.pos.sales)}</p>
                  <p className="text-sm text-white/60">{channelStats.pos.percentage}%</p>
                </div>
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-white">Tienda Online</p>
                  <p className="text-sm text-white/60">{channelStats.web.orders} órdenes</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-white">{formatCurrency(channelStats.web.sales)}</p>
                  <p className="text-sm text-white/60">{channelStats.web.percentage}%</p>
                </div>
              </div>
            </div>
          </div>

          {/* Breakdown por Período */}
          {periodStats && (
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h4 className="text-lg font-semibold mb-4 flex items-center gap-2 text-white">
                <Calendar className="w-5 h-5" />
                Breakdown por Período
              </h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-white">Órdenes Legacy</p>
                    <p className="text-sm text-white/60">Web antes 14/07</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-white">{periodStats.legacy.count}</p>
                    <p className="text-sm text-white/60">{periodStats.legacy.percentage}%</p>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-white">Órdenes Modernas</p>
                    <p className="text-sm text-white/60">POS después 14/07</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-white">{periodStats.modern.count}</p>
                    <p className="text-sm text-white/60">{periodStats.modern.percentage}%</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// === COMPONENTE PRINCIPAL ===
const AdminReportPage = () => {
  // ✅ HOOKS REALES CONECTADOS A FIREBASE
  const { logout, navigateToTerminal } = useAuth();
  const { showToast } = useToast();
  const {
    loading,
    error,
    reportData,
    reportPeriod,
    reportSummary,
    calculateCustomReport,
    exportCurrentReport,
    exportEmployeeShifts,
    exportSalesReport,
    getChannelStatistics,
    getPeriodStatistics,
    canExport,
    exportInProgress,
    exportProgress
  } = usePayroll();

  // Estados locales
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [reportContentType, setReportContentType] = useState('all'); // Faltaba esta declaración

  // Estados para modales
  const [isShiftModalOpen, setIsShiftModalOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [selectedEmployeeForModal, setSelectedEmployeeForModal] = useState(null);
  const [detailedShiftsModal, setDetailedShiftsModal] = useState({ isOpen: false, employee: null, shifts: [] });

  // Estados para UI
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showExecutiveDashboard, setShowExecutiveDashboard] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  // Estados para filtros
  // Ahora, inicializamos los estados de fecha/hora para que reflejen la hora actual de PR
  const nowPR = getCurrentPRTime(); // Usar la función importada de formatters.js
  const [startDate, setStartDate] = useState(formatDateFns(nowPR, 'yyyy-MM-dd'));
  const [startTime, setStartTime] = useState(formatDateFns(nowPR, 'HH:mm'));
  const [endDate, setEndDate] = useState(formatDateFns(nowPR, 'yyyy-MM-dd'));
  const [endTime, setEndTime] = useState(formatDateFns(nowPR, 'HH:mm')); // Puede ser 23:59 por defecto para fin de día
  const [datePreset, setDatePreset] = useState(DEFAULT_PRESET);
  const [showCustomRange, setShowCustomRange] = useState(false);

  const navigate = useNavigate();

  // ✅ FUNCIÓN PARA OBTENER EMPLEADOS DESDE FIREBASE
  const fetchEmployees = useCallback(async () => {
    try {
      const q = query(collection(db, 'sellers'), orderBy('name'));
      const snapshot = await getDocs(q);
      setEmployees(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error('Error fetching employees:', err);
      showToast('error', 'Error al cargar la lista de empleados.');
    }
  }, [showToast]);

  // ✅ FUNCIÓN PARA OBTENER TURNOS DETALLADOS DESDE FIREBASE
  const fetchDetailedShifts = useCallback(async (employeeId, startDateTime, endDateTime) => {
    // startDateTime y endDateTime ya vienen como Date objetos,
    // Firestore los guardará como Timestamp UTC. No se necesita manipulación de zona horaria aquí.
    try {
      const shiftsQuery = query(
        collection(db, 'shifts'),
        where('employeeId', '==', employeeId),
        where('startTime', '>=', Timestamp.fromDate(startDateTime)),
        where('startTime', '<=', Timestamp.fromDate(endDateTime)),
        orderBy('startTime', 'desc')
      );

      const snapshot = await getDocs(shiftsQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (err) {
      console.error('Error fetching detailed shifts:', err);
      showToast('error', 'Error al cargar los turnos detallados.');
      return [];
    }
  }, [showToast]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // `range` opcional ({ startDate, startTime, endDate, endTime }) para generar
  // con un preset recién elegido sin esperar a que se actualice el estado.
  const handleGenerateCustomReport = useCallback(async (range) => {
    const r = range && range.startDate ? range : { startDate, startTime, endDate, endTime };
    if (r.startDate && r.endDate && r.startTime && r.endTime) {
      // Crear objetos Date a partir de las cadenas de input.
      // Estos objetos Date se crearán en la zona horaria LOCAL del navegador.
      // usePayroll ya se encarga de que la función de Firebase reciba las fechas correctas.
      const startDateTime = new Date(`${r.startDate}T${r.startTime}`);
      const endDateTime = new Date(`${r.endDate}T${r.endTime}`);

      if (endDateTime < startDateTime) {
        showToast('error', 'La fecha y hora de fin no puede ser anterior a la de inicio.');
        return;
      }

      const reportOptions = {
        includeHours: reportContentType === 'all' || reportContentType === 'hours',
        includeCommissions: reportContentType === 'all' || reportContentType === 'commissions',
      };

      showToast('info', 'Generando reporte... Esto puede tardar unos segundos.');
      await calculateCustomReport(employees, startDateTime, endDateTime, reportOptions);
      // El showToast de éxito se maneja dentro de calculateCustomReport en usePayroll
      // o se puede añadir aquí si prefieres un control más granular.
    } else {
      showToast('error', "Por favor, complete todas las fechas y horas.");
    }
  }, [startDate, startTime, endDate, endTime, reportContentType, employees, calculateCustomReport, showToast]);

  const applyPreset = useCallback((preset, range) => {
    const next = {
      startDate: formatDateFns(range.from, 'yyyy-MM-dd'),
      startTime: '00:00',
      endDate: formatDateFns(range.to, 'yyyy-MM-dd'),
      endTime: '23:59',
    };
    setDatePreset(preset);
    setStartDate(next.startDate); setStartTime(next.startTime);
    setEndDate(next.endDate); setEndTime(next.endTime);
    handleGenerateCustomReport(next);
  }, [handleGenerateCustomReport]);

  // Reporte inicial (Hoy) en cuanto cargan los empleados
  const initialReportRef = useRef(false);
  useEffect(() => {
    if (initialReportRef.current || employees.length === 0) return;
    initialReportRef.current = true;
    applyPreset(DEFAULT_PRESET, getPresetRange(DEFAULT_PRESET));
  }, [employees, applyPreset]);

  const handleExportFullReport = async () => {
    try {
      showToast('info', 'Preparando exportación del reporte completo. Se descargarán múltiples archivos CSV.');
      const result = await exportCurrentReport({
        filename: `reporte_completo_${startDate}_${endDate}`,
        includeShiftsDetail: true,
        includeSalesDetail: true,
        includeExecutiveSummary: true
      });

      if (result.success) {
        showToast('success', `Exportación completada. ${result.message}`);
      } else {
        showToast('error', `Error al exportar: ${result.error}`);
      }
    } catch (error) {
      showToast('error', `Error inesperado al exportar: ${error.message}`);
    }
  };

  const handleExportSales = async () => {
    try {
      showToast('info', 'Preparando exportación de ventas. Se descargarán múltiples archivos CSV.');
      const result = await exportSalesReport({
        filename: `ventas_unificadas_${startDate}_${endDate}`,
        includeOrderDetails: true,
        includeChannelBreakdown: true
      });

      if (result.success) {
        showToast('success', `Exportación completada. ${result.message}`);
      } else {
        showToast('error', `Error al exportar: ${result.error}`);
      }
    } catch (error) {
      showToast('error', `Error inesperado al exportar: ${error.message}`);
    }
  };

  const handleShowDetailedShifts = async (employee) => {
    // Al pasar fechas a fetchDetailedShifts, se pasan como objetos Date locales
    // Firestore los convierte a Timestamp UTC correctamente.
    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);

    if (endDateTime < startDateTime) {
      showToast('error', 'El rango de fechas del reporte no es válido. Ajuste las fechas para ver los turnos detallados.');
      return;
    }

    showToast('info', `Cargando turnos de ${employee.employeeName}...`);
    const shifts = await fetchDetailedShifts(employee.employeeId, startDateTime, endDateTime);

    if (shifts.length === 0) {
      showToast('info', `No se encontraron turnos para ${employee.employeeName} en el período seleccionado.`);
      setDetailedShiftsModal({ isOpen: false, employee: null, shifts: [] });
      return;
    }

    setDetailedShiftsModal({
      isOpen: true,
      employee,
      shifts
    });
  };

  const handleOpenCreateModal = () => {
    setEditingShift(null);
    setSelectedEmployeeForModal(null);
    setIsShiftModalOpen(true);
  };

  const handleOpenCreateModalForEmployee = (employeeId) => {
    setEditingShift(null);
    setSelectedEmployeeForModal(employeeId);
    setIsShiftModalOpen(true);
  };

  const handleOpenEditModal = (shift) => {
    setEditingShift(shift);
    setSelectedEmployeeForModal(null);
    setIsShiftModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsShiftModalOpen(false);
    setEditingShift(null);
    setSelectedEmployeeForModal(null);
  };

  const handleShiftSaved = () => {
    setTimeout(() => {
      handleGenerateCustomReport();
    }, 1000);
  };

  const toggleRowExpansion = (employeeId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(employeeId)) {
      newExpanded.delete(employeeId);
    } else {
      newExpanded.add(employeeId);
    }
    setExpandedRows(newExpanded);
  };

  const filteredData = reportData && selectedEmployee !== 'all'
    ? reportData.filter(item => {
      return item.employeeId === selectedEmployee;
    })
    : reportData;

  const channelStats = getChannelStatistics();
  const periodStats = getPeriodStatistics();

  const handleRecalculateEarnings = async () => {
    setRecalculating(true);
    showToast('info', 'Iniciando recalculo de ganancias de turnos antiguos. Esto puede tardar.');
    try {
      const { httpsCallable } = await import('firebase/functions');
      const recalculateFunction = httpsCallable(functions, 'recalculateShiftEarnings');
      const result = await recalculateFunction();

      if (result.data && result.data.success) {
        showToast('success', `Recálculo completado: ${result.data.message}`);
        handleGenerateCustomReport();
      } else {
        showToast('error', `Falló el recálculo: ${result.data?.message || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error("Error al invocar la función de recálculo:", err);
      showToast('error', `Error crítico al recalcular: ${err.message}`);
    } finally {
      setRecalculating(false);
    }
  };

  const totals = useMemo(() => (filteredData || []).reduce((acc, it) => ({
    hours: acc.hours + (it.totalHours || 0),
    earnings: acc.earnings + (it.totalEarnings || 0),
    sales: acc.sales + (it.sales || 0),
    commission: acc.commission + (it.commission || 0),
    toPay: acc.toPay + (it.totalToPay || 0),
  }), { hours: 0, earnings: 0, sales: 0, commission: 0, toPay: 0 }), [filteredData]);

  const showHours = reportContentType !== 'commissions';
  const showSales = reportContentType !== 'hours';

  const renderRowActions = (item) => (
    <>
      {item.shiftsCount > 0 && (
        <button onClick={() => handleShowDetailedShifts(item)} title="Ver turnos"
          className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 transition">
          <Eye size={14} />{item.shiftsCount > 1 ? `${item.shiftsCount} turnos` : 'Ver turno'}
        </button>
      )}
      {item.canEdit && item.editableShift && (
        <button onClick={() => handleOpenEditModal(item.editableShift)} title="Editar turno"
          className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 text-white/60 hover:text-white hover:bg-white/10 transition">
          <Edit size={14} />
        </button>
      )}
      {item.totalHours === 0 && item.commission > 0 && (
        <button onClick={() => handleOpenCreateModalForEmployee(item.employeeId)} title="Agregar turno"
          className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 transition">
          <Plus size={14} />Turno
        </button>
      )}
      {item.shiftsCount > 0 && (
        <button
          onClick={async () => {
            try {
              const result = await exportEmployeeShifts(item.employeeId);
              if (result.success) showToast('success', `✅ ${result.message}`);
            } catch (error) {
              showToast('error', `❌ Error: ${error.message}`);
            }
          }}
          title="Exportar turnos (CSV)"
          className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white/5 text-white/60 hover:text-white hover:bg-white/10 transition">
          <Download size={14} />
        </button>
      )}
      {item.totalHours === 0 && item.commission === 0 && (
        <span className="text-xs text-white/30 px-1">Sin actividad</span>
      )}
    </>
  );

  const segmentClass = (active) => `h-8 px-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${active ? 'bg-blue-600 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'}`;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="mx-auto w-full max-w-[1400px] px-4 py-5 sm:px-6 lg:px-8">
        <DashboardHeader
          title="Turnos y Nómina"
          subtitle={reportPeriod || 'Horas trabajadas, ventas y pago por empleado'}
          actions={
            <>
              <button onClick={handleOpenCreateModal} disabled={employees.length === 0}
                className="inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm font-medium bg-purple-600 hover:bg-purple-500 transition disabled:opacity-50">
                <Plus size={16} />Añadir turno
              </button>
              <button onClick={handleRecalculateEarnings} disabled={recalculating} title="Recalcular ganancias de turnos antiguos"
                className="inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm font-medium bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10 transition disabled:opacity-50">
                {recalculating ? <Loader2 size={16} className="animate-spin" /> : <Calculator size={16} />}
                <span className="hidden sm:inline">{recalculating ? 'Recalculando…' : 'Recalcular'}</span>
              </button>
              <button onClick={navigateToTerminal} title="Terminal de turnos"
                className="hidden md:inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm font-medium bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10 transition">
                <Terminal size={16} />Terminal
              </button>
            </>
          }
        />

        {/* Filtros */}
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 sm:p-4 mb-4 space-y-3">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="-mx-1 px-1 overflow-x-auto scrollbar-none">
              <DatePresetButtons value={showCustomRange ? 'custom' : datePreset} onChange={(p, r) => { setShowCustomRange(false); applyPreset(p, r); }} disabled={loading} />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => setShowCustomRange(v => !v)}
                className={`inline-flex items-center gap-2 h-10 px-3 rounded-lg text-sm font-medium border transition ${showCustomRange ? 'bg-blue-600/20 border-blue-500/40 text-blue-200' : 'bg-white/5 border-white/10 text-white/70 hover:text-white'}`}>
                <Calendar size={16} />Personalizado
              </button>
              <div className="flex w-full sm:w-auto items-center gap-1 rounded-lg border border-white/10 bg-white/5 p-1 sm:ml-auto lg:ml-0 [&>button]:flex-1 sm:[&>button]:flex-none">
                {[['all', 'Todo'], ['hours', 'Horas'], ['commissions', 'Comisiones']].map(([type, label]) => (
                  <button key={type} onClick={() => setReportContentType(type)} className={segmentClass(reportContentType === type)}>{label}</button>
                ))}
              </div>
            </div>
            {loading && <Loader2 className="w-5 h-5 animate-spin text-white/50 hidden lg:block" />}
          </div>

          {showCustomRange && (
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end pt-1">
              <div>
                <label className="text-xs font-medium text-white/50">Desde</label>
                <div className="flex gap-2 mt-1">
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                    className="min-w-0 flex-1 bg-white/5 border border-white/10 rounded-lg h-10 px-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50" />
                  <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
                    className="w-28 bg-white/5 border border-white/10 rounded-lg h-10 px-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-white/50">Hasta</label>
                <div className="flex gap-2 mt-1">
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                    className="min-w-0 flex-1 bg-white/5 border border-white/10 rounded-lg h-10 px-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50" />
                  <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
                    className="w-28 bg-white/5 border border-white/10 rounded-lg h-10 px-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50" />
                </div>
              </div>
              <button onClick={() => handleGenerateCustomReport()} disabled={loading}
                className="inline-flex items-center justify-center gap-2 h-10 px-5 rounded-lg font-semibold bg-emerald-600 hover:bg-emerald-500 transition disabled:opacity-50">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Filter size={16} />}Generar
              </button>
            </div>
          )}

          {/* Empleados */}
          <div className="flex gap-2 overflow-x-auto scrollbar-none -mx-1 px-1">
            {[{ id: 'all', name: 'Todos' }, ...employees.filter(e => e.active !== false)].map(emp => (
              <button key={emp.id} onClick={() => setSelectedEmployee(emp.id)}
                className={`shrink-0 h-8 px-3 rounded-full text-sm font-medium border transition ${selectedEmployee === emp.id
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white/5 border-white/10 text-white/60 hover:text-white'}`}>
                {emp.name}
              </button>
            ))}
          </div>
        </div>

        {/* Resumen */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {[
            { label: 'Horas', value: formatDuration(totals.hours), icon: Clock, color: 'text-blue-400', show: showHours },
            { label: 'Pago por horas', value: formatCurrency(totals.earnings), icon: DollarSign, color: 'text-cyan-400', show: showHours },
            { label: 'Ventas', value: formatCurrency(totals.sales), icon: ShoppingBag, color: 'text-purple-400', show: showSales },
            { label: reportContentType === 'all' ? 'Total a pagar' : 'Comisiones', value: formatCurrency(reportContentType === 'all' ? totals.toPay : totals.commission), icon: TrendingUp, color: 'text-emerald-400', show: true },
          ].filter(c => c.show).map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="rounded-xl border border-white/10 bg-white/[0.03] p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-white/50 uppercase tracking-wide">{label}</p>
                <Icon className={`h-4 w-4 ${color}`} />
              </div>
              <p className={`mt-1 text-lg sm:text-2xl font-bold ${color}`}>{loading ? '…' : value}</p>
            </div>
          ))}
        </div>

        {/* Resultados */}
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 sm:p-4 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="text-base sm:text-lg font-semibold">Empleados{filteredData ? ` (${filteredData.length})` : ''}</h2>
            <div className="flex gap-2">
              <button onClick={handleExportFullReport} disabled={!canExport || exportInProgress}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10 transition disabled:opacity-40">
                {exportInProgress ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet size={14} />}Reporte CSV
              </button>
              <button onClick={handleExportSales} disabled={!canExport || exportInProgress}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10 transition disabled:opacity-40">
                <Download size={14} />Ventas CSV
              </button>
            </div>
          </div>

          {loading && (
            <div className="flex justify-center items-center h-32">
              <Loader2 className="w-7 h-7 animate-spin text-white/40" />
            </div>
          )}

          {error && !loading && (
            <div className="text-red-300 text-sm text-center p-3 bg-red-500/10 border border-red-500/20 rounded-lg">{error}</div>
          )}

          {!loading && filteredData && filteredData.length > 0 && (
            <>
              {/* Móvil: tarjetas */}
              <div className="md:hidden space-y-2">
                {filteredData.map(item => (
                  <div key={item.employeeId} className="rounded-lg border border-white/10 bg-gray-900/60 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{item.employeeName}</p>
                        <p className="text-xs text-white/40">{formatCurrency(item.hourlyRate || 10.5)}/h · {item.shiftsCount || 0} turno{item.shiftsCount === 1 ? '' : 's'}</p>
                      </div>
                      {reportContentType === 'all' && (
                        <div className="text-right shrink-0">
                          <p className="text-[10px] uppercase text-white/40">A pagar</p>
                          <p className="text-lg font-bold text-emerald-400">{formatCurrency(item.totalToPay)}</p>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                      {showHours && <div className="rounded-md bg-white/5 py-1.5"><p className="text-[10px] text-white/40">Horas</p><p className="text-sm font-medium">{formatDuration(item.totalHours)}</p></div>}
                      {showHours && <div className="rounded-md bg-white/5 py-1.5"><p className="text-[10px] text-white/40">Pago horas</p><p className="text-sm font-medium">{formatCurrency(item.totalEarnings)}</p></div>}
                      {showSales && <div className="rounded-md bg-white/5 py-1.5"><p className="text-[10px] text-white/40">Ventas</p><p className="text-sm font-medium">{formatCurrency(item.sales)}</p></div>}
                      {showSales && <div className="rounded-md bg-white/5 py-1.5"><p className="text-[10px] text-white/40">Órdenes</p><p className="text-sm font-medium">{item.orders}</p></div>}
                      {showSales && <div className="rounded-md bg-white/5 py-1.5"><p className="text-[10px] text-white/40">Comisión</p><p className="text-sm font-medium">{formatCurrency(item.commission)}</p></div>}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">{renderRowActions(item)}</div>
                  </div>
                ))}
              </div>

              {/* Escritorio: tabla */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-white/50">
                      <th className="text-left py-2.5 px-3 font-medium">Empleado</th>
                      {showHours && <th className="text-right py-2.5 px-3 font-medium">Horas</th>}
                      {showHours && <th className="text-right py-2.5 px-3 font-medium">Pago horas</th>}
                      {showSales && <th className="text-right py-2.5 px-3 font-medium">Ventas</th>}
                      {showSales && <th className="text-right py-2.5 px-3 font-medium">Órdenes</th>}
                      {showSales && <th className="text-right py-2.5 px-3 font-medium">Comisión</th>}
                      {reportContentType === 'all' && <th className="text-right py-2.5 px-3 font-medium">Total a pagar</th>}
                      <th className="text-right py-2.5 px-3 font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredData.map(item => (
                      <tr key={item.employeeId} className="hover:bg-white/[0.03] transition">
                        <td className="py-2.5 px-3">
                          <p className="font-medium">{item.employeeName}</p>
                          <p className="text-xs text-white/40">{formatCurrency(item.hourlyRate || 10.5)}/h</p>
                        </td>
                        {showHours && <td className="text-right py-2.5 px-3">{formatDuration(item.totalHours)}</td>}
                        {showHours && <td className="text-right py-2.5 px-3">{formatCurrency(item.totalEarnings)}</td>}
                        {showSales && <td className="text-right py-2.5 px-3">{formatCurrency(item.sales)}</td>}
                        {showSales && <td className="text-right py-2.5 px-3">{item.orders}</td>}
                        {showSales && <td className="text-right py-2.5 px-3">{formatCurrency(item.commission)}</td>}
                        {reportContentType === 'all' && <td className="text-right py-2.5 px-3 font-bold text-emerald-400">{formatCurrency(item.totalToPay)}</td>}
                        <td className="py-2.5 px-3"><div className="flex justify-end gap-1.5">{renderRowActions(item)}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {!loading && filteredData && filteredData.length === 0 && (
            <p className="text-center text-white/40 py-10 text-sm">No hay turnos ni ventas en el período seleccionado.</p>
          )}
          {!loading && !filteredData && !error && (
            <p className="text-center text-white/40 py-10 text-sm">Elige un período para ver el reporte.</p>
          )}
        </div>

        {/* Dashboard Ejecutivo (plegable) */}
        <ExecutiveDashboard
          reportSummary={reportSummary}
          channelStats={channelStats}
          periodStats={periodStats}
          isVisible={showExecutiveDashboard}
          onToggle={() => setShowExecutiveDashboard(!showExecutiveDashboard)}
        />
      </div>

      {/* Modal de Gestión de Turnos */}
      <ShiftEditorModal
        isOpen={isShiftModalOpen}
        onClose={handleCloseModal}
        shift={editingShift}
        employees={employees}
        onSave={handleShiftSaved}
        preselectedEmployeeId={selectedEmployeeForModal}
      />

      {/* Modal de Turnos Detallados */}
      <DetailedShiftsModal
        isOpen={detailedShiftsModal.isOpen}
        onClose={() => setDetailedShiftsModal({ isOpen: false, employee: null, shifts: [] })}
        employee={detailedShiftsModal.employee}
        shifts={detailedShiftsModal.shifts}
        onEditShift={handleOpenEditModal}
      />
    </div>
  );
};

export default AdminReportPage;
